import { useEffect, useState } from 'react'

import { FormContext, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Box, Button, Grid } from '@material-ui/core'
import { reverse } from 'named-urls'

import { LogoInput, LoadingFeedback } from 'components'
import {
  OrganizationUser,
  MainForm,
  ResponsibleDpoForm,
  PortalDpoForm,
  OrganizationCompany,
} from './components'
import { ChangeResponsibleDialog } from './components/ResponsibleDpoForm/components'

import { usePermissions, useSnackbar } from 'hooks'

import helpers from 'helpers'
import * as service from 'service'
import constants from 'constants/index'
import { routes } from 'Routes'

import useStyles from './styles'
import schema from './schema'

const OrganizationForm = ({
  editionMode = false,
  editionPermitted,
  organization,
  refresh,
  isOrganizationLoading,
}) => {
  const [loading, setLoading] = useState(false)
  const [changeResponsibleDialog, setChangeResponsibleDialog] = useState(false)
  const [logoFile, setLogoFile] = useState(null)

  const history = useHistory()
  const classes = useStyles()
  const permissions = usePermissions()
  const snackbar = useSnackbar()
  const formMethods = useForm({
    validationSchema: schema(editionMode),
    defaultValues: {
      name: '',
      kind: '',
      responsible: '',
      dpo: '',
      substitute: '',
      cpf: '',
      cnpj: '',
      document: '',
      documentKind: 'cpf',
      selectExistentResponsible: false,
      responsibleDpo: null,
      responsibleName: '',
      responsibleEmail: '',
    },
  })

  const { handleSubmit, reset, watch } = formMethods

  useEffect(() => {
    if (organization) {
      reset({
        name: organization?.name || '',
        kind: organization?.kind || '',
        responsible: organization?.responsible || '',
        dpo: organization?.dpo || '',
        substitute: organization?.substitute || '',
        cpf: organization?.cpf || '',
        cnpj: organization?.cnpj || '',
        documentKind:
          organization?.kindPerson === constants.organizations.JURIDIC_KIND &&
          organization?.kind === constants.organizations.DPO_KIND
            ? 'cnpj'
            : 'cpf',
        responsibleDpo: organization?.responsibleUser || '',
        responsibleName: organization?.responsibleUser?.name || '',
        responsibleEmail: '',
      })
    }
    //eslint-disable-next-line
  }, [organization])

  const kind = watch('kind')
  const isDPO = kind === constants.organizations.DPO_KIND

  const onSubmit = async (data) => {
    setLoading(true)

    try {
      if (data?.kind === constants.organizations.DPO_KIND) {
        data.kindPerson = data.documentKind === 'cnpj' ? 'juridic' : 'personal'
      }

      if (organization) {
        let response = await service.backoffice.organizations.update({
          organizationId: organization?.id,
          organization: data,
        })

        if (logoFile) {
          await service.advisor.organizations.uploadLogo({
            organizationId: response?.data?.organization?.id,
            logo: logoFile,
          })
        }

        snackbar.open({
          message: 'Grupo Advisor editado com sucesso!',
          variant: 'success',
        })

        refresh()

        history.push(routes.organizations.all)
      } else {
        let response = await service.backoffice.organizations.create({
          organization: data,
        })

        snackbar.open({
          message: 'Grupo Advisor criado com sucesso!',
          variant: 'success',
        })

        history.push(
          reverse(routes.organizations.edit, {
            organizationId: response?.data?.organization?.id,
          }),
        )
      }
    } catch (error) {
      handleError(error, data)
    } finally {
      setLoading(false)
    }
  }

  const handleError = (error, data) => {
    setLoading(false)
    reset(data)
    snackbar.open({
      message: helpers.formatters.errorMessage(
        error?.response?.data?.error,
        false,
      ),
      variant: 'error',
    })
  }

  const showUsers =
    permissions.permitted(constants.permissions.ORGANIZATION_USER.LIST) ||
    permissions.permitted(constants.permissions.ORGANIZATION_USER.MANAGE)

  const showCompanies =
    permissions.permitted(constants.permissions.ORGANIZATION_COMPANY.VIEW) ||
    permissions.permitted(constants.permissions.ORGANIZATION_COMPANY.MANAGE)

  return (
    <>
      <LoadingFeedback open={loading || isOrganizationLoading} />
      <Grid container spacing={4}>
        <FormContext {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} className={classes.fullWidth}>
            <Box>
              <MainForm
                editionPermitted={editionPermitted}
                editionMode={editionMode}
                isDPO={isDPO}
              />
            </Box>

            <Box mt={4}>
              <ResponsibleDpoForm
                disableCertFields={editionMode}
                setChangeResponsibleDialog={setChangeResponsibleDialog}
              />
            </Box>

            {isDPO && (
              <Box mt={4}>
                <PortalDpoForm editionPermitted={editionPermitted} />
              </Box>
            )}

            {editionMode && (
              <>
                <Box mt={4}>
                  <LogoInput
                    logo={organization?.logo}
                    setLogoFile={setLogoFile}
                    editionPermitted={editionPermitted}
                    isUrlComplete
                  />
                </Box>

                {showUsers && (
                  <Box mt={4}>
                    <OrganizationUser organization={organization} />
                  </Box>
                )}

                {showCompanies && (
                  <Box mt={4}>
                    <OrganizationCompany organization={organization} />
                  </Box>
                )}
              </>
            )}

            {editionPermitted && (
              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button type="submit" variant="contained" color="primary">
                  {organization ? 'Salvar' : 'Salvar e criar Grupo Advisor'}
                </Button>
              </Box>
            )}
          </form>
          {organization && editionPermitted && (
            <ChangeResponsibleDialog
              open={changeResponsibleDialog}
              setOpen={setChangeResponsibleDialog}
              organizationId={organization?.id}
              refresh={refresh}
            />
          )}
        </FormContext>
      </Grid>
    </>
  )
}

OrganizationForm.propTypes = {
  editionMode: PropTypes.bool,
  editionPermitted: PropTypes.bool,
  organization: PropTypes.object,
}

export default OrganizationForm
